import { joiResolver } from '@hookform/resolvers/joi'
import React, { useCallback, useContext, useState } from 'react'
import { ProgressBar } from 'react-bootstrap'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { FormProvider, useForm } from 'react-hook-form'

import StepContactInfo from '@/components/form/steps/contact-info'
import StepMail from '@/components/form/steps/mail'
import StepThanks from '@/components/form/steps/thanks'
import { formMessages } from '@/constants/form-messages'
import { config } from '@/internal/config/config'
import { type FormWebsite } from '@/internal/types/form'
import {
  schemaStepContactInfo,
  schemaStepEmail,
  schemaStepSelectContents,
  schemaStepSelectContentsContactExist
} from '@/internal/validators/validators'

import { ErrorMessage } from './error-message'
import Submit from './fields/submit'
import * as styles from './form.module.scss'
import FormContext from './form-context'
import {
  getMatomoUserId,
  hasVariousRecordsIds,
  matomoTrackForm,
  submitForm
} from './form-utils'
import StepSelectContents from './steps/select-contents'

type FormStepsProps = {
  values: FormWebsite
  title?: string
  subtitle?: string
}

const FormSteps: React.FC<FormStepsProps> = ({ values, title, subtitle }) => {
  const { form } = values
  const labels = formMessages[form]
  const [formSubmitError, setFormSubmitError] = useState(false)
  const [step, setStep] = useState(1)
  const formContext = useContext(FormContext)
  const { executeRecaptcha } = useGoogleReCaptcha()

  const methods = useForm({
    mode: 'onChange',
    resolver: async (data, context, options) => {
      let schema
      switch (data.step) {
        case 1:
          schema = schemaStepEmail
          break
        case 2:
          schema = schemaStepContactInfo
          break
        case 3:
          schema = data.newcontact
            ? schemaStepSelectContents
            : schemaStepSelectContentsContactExist
          break
        default:
          schema = {}
      }

      return await joiResolver(schema)(data, context, options)
    },
    defaultValues: {
      ...values,
      step: 1,
      newcontact: true
    }
  })

  const { getValues, setValue, formState, handleSubmit } = methods
  const onSubmit = useCallback(async () => {
    setFormSubmitError(false)

    if (!executeRecaptcha) {
      return
    }

    const recaptchaAction = `${form}step${step}`
    const result = await executeRecaptcha(recaptchaAction)

    // Establecer valores en el formulario
    setValue('token', result)
    const data = getValues()

    try {
      const success = await submitForm({ data })
      const { step: nextStep } = success
      if (nextStep === undefined) {
        throw new Error('Step not found')
      }
      Object.entries(success).forEach(([key, value]) => {
        setValue(key as keyof FormWebsite, value as never)
      })
      const currentUserId = getMatomoUserId() // Síncrono
      const matomoUID = getValues('matomoUID')

      if (window?._paq && !currentUserId && matomoUID) {
        //console.info('Setting Matomo User ID:', matomoUID)
        window._paq.push(['setUserId', matomoUID])
      } else {
        //console.warn('Matomo User ID not set:', currentUserId, matomoUID)
      }

      setStep(nextStep)
      if (nextStep === 4) {
        const token = await executeRecaptcha(recaptchaAction)
        const allDownloads = getValues('downloads')
        const formData = getValues()

        const selectedDownloads = allDownloads?.filter((download) => {
          const selectionField = `contentSelected-${download.originalId}`
          return formData[selectionField] === true || download.required
        })

        setValue('token', token)
        setValue('step', nextStep)
        //console.info('Submitting form with downloads:', getValues())
        await submitForm({
          data: getValues(),
          save: true
        })
        void matomoTrackForm(selectedDownloads, form, form)

        // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
      }
    } catch (error) {
      setFormSubmitError(true)
      console.error(error)
    }
  }, [executeRecaptcha, form, step, getValues, setValue])

  const onInvalid = (): void => {
    if (config.env.isDev || config.env.isTest) {
      Object.entries(formState.errors).forEach(([field, error]) => {
        console.error(field, error)
      })
    }
  }

  return (
    <FormProvider {...methods}>
      <div
        className={[
          styles.form,
          formContext?.classes?.root,
          values.form === 'paperDetail' ? styles.formDownloads : ''
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {formSubmitError && <ErrorMessage />}
        {!formSubmitError && (
          <form
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={handleSubmit(onSubmit, onInvalid)}
            name={form}
            method="POST"
            id={form}
            data-testid="form"
          >
            <div>
              <ProgressBar
                variant="custom"
                now={step}
                max={4}
                style={{ height: '4px' }}
                className={styles.progressBar}
              />
            </div>

            <div className={styles.content}>
              {step === 1 && (
                <StepMail
                  title={title ?? labels.stepMailTitle}
                  subtitle={subtitle ?? labels.stepMailSubtitle}
                />
              )}
              {step === 2 && <StepContactInfo />}
              {step === 3 && <StepSelectContents />}
              {step === 4 && <StepThanks />}

              {step < 4 && (
                <Submit
                  isSubmitting={formState.isSubmitting}
                  value={
                    hasVariousRecordsIds(values) && step > 1
                      ? labels.buttonMultiple
                      : labels.submit
                  }
                />
              )}
            </div>
          </form>
        )}
      </div>
    </FormProvider>
  )
}

export default FormSteps
